import React from 'react';

/**
 * Displays the appropriate CPT Quantity string depending on the order's billing method.
 *
 * A CPT quantity is in the format 'x 19 / 2' with the first number being the quantity billed to commercial insurance
 *  and the second number being the quantity billed to Medicare. This component determines whether to show the first
 *  number, the second number, or both numbers.
 * 
 * This component also supports an "editing" mode where the quantity is displayed as a <input> that can be updated.
 *
 * @param {map} cptQuantityMap The map of updated CPT Quantity values. key={omsInputFieldId}, value={cptQuantity}
 * @param {object} inputField The OmsInputField containing the field ID and the original CPT Quantity string
 * @param {boolean} splitCptQty Whether the CPT Quantity should be split based on billing method or displayed in full
 * @param {boolean} isMedicareOrder Whether the order's billing method is Medicare or not
 * @param {boolean} canEdit A flag indicating if the CPT Quantity can be edited
 * @param {function} onQtyChange The function to call when the CPT Quantity is updated
 */
export default function CptQuantity({ cptQuantityMap, inputField, splitCptQty, isMedicareOrder, canEdit, onQtyChange }) {
	if (!splitCptQty || !inputField?.cptQuantity) {
		return inputField?.cptQuantity || null;
	}

	const qty = getUpdatedCptQuantityOrDefault(inputField, cptQuantityMap, isMedicareOrder);

	if (isMedicareOrder && canEdit && onQtyChange) {
		const max = getEffectiveCptQuantity(inputField.cptQuantity, isMedicareOrder);
		const handleChange = (event) => {
			let value = event.target?.value.replace(/\D/, '');
			if (value) {
				// Do not allow a value lower than 1 or higher than the original quantity
				value = Math.min(Math.max(Number(value),1), max) + '';
			}
			onQtyChange(inputField.id, value);
		}

		return <input type="number" min={1} max={max} value={qty} onChange={handleChange} style={{textAlign: 'center'}} size="4" />;
	}

	return `x ${qty}`;
}

/**
 * Returns the appropriate CPT quantity based on the order's billing type.
 * 
 * A CPT quantity is in the format 'x 19 / 2' with the first number being the quantity billed to commercial insurance
 *  and the second number being the quantity billed to Medicare.
 *
 * @param {string} combinedQuantity The full CPT Quantity text containing both commercial and Medicare quantities
 * @param {boolean} isMedicareOrder A flag indicating if the order is Medicare
 */
export const getEffectiveCptQuantity = (combinedQuantity, isMedicareOrder) => {
	// Strip out the 'x' and split the string into two pieces: 'x 19 / 2' => [' 19 ', ' 2']
	let quantity = combinedQuantity?.replaceAll('x', '');
	const [commercialQty, medicareQty] = quantity?.split('/') || [];

	// Use the Medicare quantity if we have one and this is a Medicare order. Otherwise, use the commerical quantity.
	if (isMedicareOrder && medicareQty?.trim()) {
		quantity = medicareQty?.trim();
	} else {
		quantity = commercialQty?.trim();
	}

	return quantity;
}

/**
 * Returns the CPT quantity value to display. For Medicare orders, try to read the quantity from the update map.
 *  Otherwise, or if no updated quantity is found, use the default value.
 */
export const getUpdatedCptQuantityOrDefault = (field, cptQuantityMap, isMedicareOrder) => {
	if (!field?.id || !field?.cptQuantity) {
		return null;
	}

	// Quantity can only be updated for Medicare orders. Ignore the map and return the default quantity string for non-Medicare orders.
	if (!isMedicareOrder) {
		return getEffectiveCptQuantity(field.cptQuantity, false);
	}

	if (cptQuantityMap) {
		for (const [cptCode, quantity] of Object.entries(cptQuantityMap)) {
			if (field.id === Number(cptCode)) {
				return quantity;
			}
		}
	}

	// Quantity was not found in the map. Return the default quantity string
	return getEffectiveCptQuantity(field.cptQuantity, isMedicareOrder);
}